import { User } from 'firebase/auth';
import { useState, useCallback, useEffect, useContext, useMemo } from 'react';
import { getCoupons } from '~/restApi/coupon';

import { useRecoilValue } from 'recoil';
import itemsTotalPerStoreMapSelector from '~/recoil/selectors/itemsTotalPerStoreMap';

import { DisplayStoresContext } from '~/provider/DisplayStoresProvider';

import * as fp from 'lodash/fp';

import { Coupon } from '~/types/coupon';
import { FetchingDataStatus } from '~/types/utilities';
import { PLATFORM_ID } from '~/globalVariables';

import getDiscountFn from '~/utils/getDiscountFn';

export default function useCoupons(auth: User | null) {
  const itemsTotalPerStoreMap = useRecoilValue(itemsTotalPerStoreMapSelector);
  const { stores } = useContext(DisplayStoresContext);

  const [coupons, setCoupons] = useState<Array<Coupon>>([]);
  const [couponFetchingDataStatus, setCouponFetchingDataStatus] = useState(FetchingDataStatus.INIT);

  const handleGetCoupon = useCallback(async (platformId: string, auth: User) => {
    try {
      setCouponFetchingDataStatus(FetchingDataStatus.LOADING);
      const res = await getCoupons(platformId, auth);
      setCouponFetchingDataStatus(FetchingDataStatus.SUCCESS);
      setCoupons(res);
    } catch (err) {
      setCouponFetchingDataStatus(FetchingDataStatus.FAIL);
    }
  }, []);

  useEffect(() => {
    if (auth) {
      handleGetCoupon(PLATFORM_ID, auth);
    }
  }, [auth, handleGetCoupon]);

  const storesNameMap = useMemo(() => {
    return new Map(stores.map(({ objectID, name }) => [objectID, name]));
  }, [stores]);

  const couponsWithStoreName = useMemo(() => {
    return coupons.map((coupon) => ({
      ...coupon,
      storesName: coupon.stores.map((id) => storesNameMap.get(id) || '').filter((name) => !!name),
    }));
  }, [coupons, storesNameMap]);

  const {
    displayAvailableCoupons,
    displayNotAvailableCoupons,
    notDisplayAvailableCoupons,
    notDisplayNotAvailableCoupons,
  } = useMemo(() => {
    const [displayCoupons, notDisplayCoupons] = fp.partition<
      Coupon & { storesName: Array<string> }
    >(fp.prop('allowDisplay'))(couponsWithStoreName);

    const [displayAvailableCoupons, displayNotAvailableCoupons] = fp.partition<
      Coupon & { storesName: Array<string> }
    >(({ stores, condition, type }) => {
      const costLimit = fp.isNumber(condition[type]?.costLimit)
        ? condition[type]?.costLimit || 0
        : Infinity;

      return (
        stores.some((id) => itemsTotalPerStoreMap.has(id)) &&
        fp.gte(fp.sum(stores.map((id) => itemsTotalPerStoreMap.get(id) || 0)), costLimit)
      );
    })(displayCoupons);

    const [notDisplayAvailableCoupons, notDisplayNotAvailableCoupons] = fp.partition<
      Coupon & { storesName: Array<string> }
    >(({ stores, condition, type }) => {
      const costLimit = fp.isNumber(condition[type]?.costLimit)
        ? condition[type]?.costLimit || 0
        : Infinity;

      return (
        stores.some((id) => itemsTotalPerStoreMap.has(id)) &&
        fp.gte(fp.sum(stores.map((id) => itemsTotalPerStoreMap.get(id) || 0)), costLimit)
      );
    })(notDisplayCoupons);

    return {
      displayAvailableCoupons,
      displayNotAvailableCoupons,
      notDisplayAvailableCoupons,
      notDisplayNotAvailableCoupons,
    };
  }, [couponsWithStoreName, itemsTotalPerStoreMap]);

  return {
    displayAvailableCoupons,
    displayNotAvailableCoupons,
    notDisplayAvailableCoupons,
    notDisplayNotAvailableCoupons,
    couponFetchingDataStatus,
  };
}
